import { useFreeShippingMeter } from "@/queries/cart";
import { memo } from "react";
import { FreeShippingPrompt } from "./FreeShippingPrompt";

export interface CartFreeShippingMeterProps {
  /**
   * The seller-cart to show the meter for. One meter per seller-cart, matching
   * the one-shipping-per-supplier model.
   */
  cartId: string;
  /**
   * The cart's item subtotal (store currency's major unit).
   */
  itemSubtotal: number;
  /**
   * The cart's selected shipping option
   * (`cart.shipping_methods[0]?.shipping_option_id`). A threshold makes one
   * service free, so the meter hides when the buyer has picked a different
   * one - see {@link useFreeShippingMeter}.
   */
  selectedShippingOptionId?: string | null;
  /**
   * Forwarded to {@link FreeShippingPrompt}. Set `false` when another visible
   * meter already announces this same cart's threshold on the page.
   */
  announce?: boolean;
  /**
   * Additional CSS classes forwarded to the prompt.
   */
  className?: string;
}

/**
 * Free-freight meter for a single seller-cart, for use in the cart view.
 *
 * Fetches the seller-cart's shipping options and derives its free-freight
 * threshold (same source checkout uses), then renders {@link FreeShippingPrompt}
 * with how much more the basket needs. Renders nothing when the cart has no
 * threshold configured or already qualifies — so the salon only sees a meter
 * when there is distance left to close.
 */
export const CartFreeShippingMeter = memo(function CartFreeShippingMeter({
  cartId,
  itemSubtotal,
  selectedShippingOptionId,
  announce,
  className
}: CartFreeShippingMeterProps) {
  const {
    freeShippingThreshold,
    amountToFreeShipping,
    freeShippingSavings,
    freeShippingOptionName
  } = useFreeShippingMeter({
    cartId,
    itemSubtotal,
    selectedShippingOptionId
  });

  return (
    <FreeShippingPrompt
      amountToFreeShipping={amountToFreeShipping}
      freeShippingThreshold={freeShippingThreshold}
      shippingSavings={freeShippingSavings}
      shippingOptionName={freeShippingOptionName}
      announce={announce}
      className={className}
    />
  );
});
